import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import apiClient from "@/api/client";

/**
 * Self-service password change for the currently logged-in user.
 * Mirrors the admin reset-password form (DevAccountsPage), but targets
 * the /auth/me/change-password/ endpoint — no old password required.
 */
export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const mut = useMutation({
    mutationFn: (pwd: string) =>
      apiClient.post(`/auth/me/change-password/`, { password: pwd }),
    onSuccess: () => setSuccess(true),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { errors?: { password?: string } } } })
          ?.response?.data?.errors?.password ?? "Błąd podczas zmiany hasła.";
      setError(msg);
    },
  });

  const handleSave = () => {
    setError("");
    if (password.length < 8) {
      setError("Hasło musi mieć co najmniej 8 znaków.");
      return;
    }
    if (password !== confirm) {
      setError("Hasła nie są zgodne.");
      return;
    }
    mut.mutate(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Zmiana hasła
        </h2>
        {success ? (
          <div className="text-sm text-green-600 dark:text-green-400 mb-4">
            Hasło zostało zmienione.
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Nowe hasło
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min. 8 znaków"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Powtórz hasło
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {success ? "Zamknij" : "Anuluj"}
          </button>
          {!success && (
            <button
              onClick={handleSave}
              disabled={mut.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {mut.isPending ? "Zapisywanie…" : "Zmień hasło"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
